import React, { useRef, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  useOutsideClick,
  useTheme,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

interface AleaSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  labelFontWeight?: number;
  isRequired?: boolean;
  enableNewLivingArea?: boolean;
  onNewLivingAreaClick?: () => void;
  newOptionText?: string;
}

const AleaSelect: React.FC<AleaSelectProps> = ({
  label,
  value,
  options,
  onChange,
  error,
  placeholder,
  labelFontWeight,
  isRequired,
  enableNewLivingArea = true,
  onNewLivingAreaClick,
  newOptionText,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  const formLabelStyles = {
    fontWeight: labelFontWeight ?? (isRequired ? 500 : 300),
    marginBottom: '3px',
    color: 'alea.500',
    paddingLeft: '16px',
  } as const;

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
  };

  return (
    <FormControl isInvalid={!!error} ref={containerRef}>
      <FormLabel sx={formLabelStyles}>{label}</FormLabel>
      <Box position="relative">
        <Flex
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={!!error}
          data-invalid={error ? '' : undefined}
          onClick={() => setIsOpen((s) => !s)}
          alignItems="center"
          justifyContent="space-between"
          borderTopLeftRadius="20px"
          borderTopRightRadius="20px"
          borderBottomLeftRadius={isOpen ? '0' : '20px'}
          borderBottomRightRadius={isOpen ? '0' : '20px'}
          border="1px solid"
          borderColor={error ? 'invalid.500' : '#DCDCDC'}
          borderBottom={!isOpen ? (error ? '1px solid invalid.500' : '1px solid #C6C6C6') : '0'}
          h="50px"
          bg="white"
          boxShadow={error ? 'sm' : 'none'}
        >
          <Text
            pl="16px"
            pr="12px"
            flex="1"
            noOfLines={1}
            color={value ? 'black' : '#C3C3C3'}
          >
            {value || placeholder || ''}
          </Text>
          <Flex
            align="center"
            justify="center"
            w="50px"
            h="50px"
            bg={theme.colors.alea[500]}
            borderTopRightRadius="20px"
            borderBottomRightRadius={isOpen ? '0' : '20px'}
          >
            {isOpen ? (
              <ChevronUpIcon color="white" boxSize={6} />
            ) : (
              <ChevronDownIcon color="white" boxSize={6} />
            )}
          </Flex>
        </Flex>

        {isOpen && (
          <Box
            position="absolute"
            top="49px"
            left={0}
            w="100%"
            bg="white"
            border="1px solid"
            borderColor={error ? 'invalid.500' : '#DCDCDC'}
            borderTopLeftRadius="0"
            borderTopRightRadius="0"
            borderBottomLeftRadius="20px"
            borderBottomRightRadius="20px"
            boxShadow={error ? 'sm' : 'sm'}
            zIndex={10}
            overflow="hidden"
          >
            {options.map((opt) => (
              <Box
                key={opt}
                role="option"
                aria-selected={opt === value}
                px="16px"
                py="12px"
                cursor="pointer"
                _hover={{ bg: '#F7FAFC' }}
                borderBottom="1px solid #EFEFEF"
                onClick={() => handleSelect(opt)}
              >
                <Text>{opt}</Text>
              </Box>
            ))}
            {enableNewLivingArea && (
              <Box px="16px" py="12px">
                <Text
                  as="span"
                  color="alea.500"
                  textDecoration="underline"
                  cursor="pointer"
                  onClick={() => {
                    setIsOpen(false);
                    onNewLivingAreaClick?.();
                  }}
                >
                  {newOptionText || t('newLivingArea')}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {error && (
        <FormErrorMessage
          pl="16px"
          mt="3px"
          color="invalid.500"
          fontSize="15px"
          fontWeight={300}
        >
          {error}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default AleaSelect;


